<template>
  <div class="turnover-center">
    <section class="hero-card">
      <div class="hero-copy">
        <span class="mode-badge">{{ $t('Current mode: Turnover Rebate') }}</span>
        <h2>{{ $t('Turnover Rebate') }}</h2>
        <p>{{ $t('Turnover agent tip') }} <strong>{{ rebateRate }}%</strong></p>
      </div>
      <button type="button" class="share-btn" @click="$router.push('/shareMoney')">
        {{ $t('Share & Earn') }}
      </button>
    </section>

    <section class="rebate-overview">
      <div class="rebate-card today">
        <span class="card-label">{{ $t('Today Rebate') }}</span>
        <strong>¥{{ fmt(rebateSummary.today) }}</strong>
      </div>
      <div class="rebate-card">
        <span class="card-label">{{ $t('Total Rebate') }}</span>
        <strong>¥{{ fmt(rebateSummary.total) }}</strong>
      </div>
      <div class="rebate-card pending">
        <span class="card-label">{{ $t('Pending Rebate') }}</span>
        <strong>¥{{ fmt(rebateSummary.pending) }}</strong>
      </div>
    </section>

    <section class="data-overview">
      <div class="section-heading">
        <div>
          <span class="eyebrow">{{ rangeLabel }}</span>
          <h3>{{ $t('Agent Overview') }}</h3>
        </div>
        <span class="member-count">{{ summary.direct_count || 0 }} {{ $t('people') }}</span>
      </div>
      <div class="overview-grid">
        <div class="overview-item">
          <span>{{ $t('Period Recharge') }}</span>
          <strong>¥{{ fmt(summary.recharge) }}</strong>
        </div>
        <div class="overview-item featured">
          <span>{{ $t('Period Turnover') }}</span>
          <strong>¥{{ fmt(summary.turnover) }}</strong>
        </div>
        <div class="overview-item">
          <span>{{ $t('Account Balance') }}</span>
          <strong>¥{{ fmt(summary.balance) }}</strong>
        </div>
        <div class="overview-item win">
          <span>{{ $t('Period Win') }}</span>
          <strong>+¥{{ fmt(summary.win_amount) }}</strong>
        </div>
        <div class="overview-item loss">
          <span>{{ $t('Period Loss') }}</span>
          <strong>-¥{{ fmt(summary.loss_amount) }}</strong>
        </div>
        <div class="overview-item" :class="netClass(summary.net_win_loss)">
          <span>{{ $t('Period Net Win/Loss') }}</span>
          <strong>{{ signedMoney(summary.net_win_loss) }}</strong>
        </div>
      </div>
    </section>

    <div class="content-tabs">
      <button
        type="button"
        :class="{ active: activeTab === 'members' }"
        @click="activeTab = 'members'"
      >
        {{ $t('Subordinate Details') }}
      </button>
      <button
        type="button"
        :class="{ active: activeTab === 'rebates' }"
        @click="activeTab = 'rebates'"
      >
        {{ $t('Commission Record') }}
      </button>
    </div>

    <section v-if="activeTab === 'members'" class="panel members-panel">
      <div class="filters">
        <div class="range-tabs">
          <button
            v-for="option in rangeOptions"
            :key="option.value"
            type="button"
            :class="{ active: range === option.value }"
            @click="pickRange(option.value)"
          >
            {{ option.label }}
          </button>
        </div>
        <div class="search-row">
          <input
            v-model="keyword"
            type="text"
            :placeholder="$t('Enter member ID or username')"
            @keyup.enter="reloadMembers"
          >
          <button type="button" @click="reloadMembers">{{ $t('Search') }}</button>
        </div>
      </div>

      <div v-if="loadingMembers" class="empty-state">{{ $t('Loading') }}...</div>
      <div v-else-if="!members.length" class="empty-state">{{ $t('No data yet') }}</div>
      <article v-for="member in members" :key="member.id" class="member-card">
        <header class="member-head">
          <div class="identity">
            <span class="avatar">{{ userInitial(member.username) }}</span>
            <div>
              <strong>{{ member.username || ('ID ' + member.id) }}</strong>
              <small>ID {{ member.id }}</small>
            </div>
          </div>
          <time>{{ $t('Registration Time') }}<br>{{ member.registered_at || '-' }}</time>
        </header>

        <div class="member-metrics primary-metrics">
          <div>
            <span>{{ $t('Recharge') }}</span>
            <strong>¥{{ fmt(member.recharge) }}</strong>
          </div>
          <div>
            <span>{{ $t('Total Turnover') }}</span>
            <strong>¥{{ fmt(member.turnover) }}</strong>
          </div>
          <div>
            <span>{{ $t("Today's Turnover") }}</span>
            <strong>¥{{ fmt(member.today_turnover) }}</strong>
          </div>
        </div>

        <div class="member-metrics result-metrics">
          <div class="win">
            <span>{{ $t('Win') }}</span>
            <strong>+{{ fmt(member.win_amount) }}</strong>
          </div>
          <div class="loss">
            <span>{{ $t('Loss') }}</span>
            <strong>-{{ fmt(member.loss_amount) }}</strong>
          </div>
          <div :class="netClass(member.net_win_loss)">
            <span>{{ $t('Net Win/Loss') }}</span>
            <strong>{{ signedNumber(member.net_win_loss) }}</strong>
          </div>
          <div>
            <span>{{ $t('Account Balance') }}</span>
            <strong>¥{{ fmt(member.balance) }}</strong>
          </div>
        </div>
      </article>

      <div class="pager" v-if="memberTotalPages > 1">
        <button type="button" :disabled="memberPage <= 1" @click="changeMemberPage(memberPage - 1)">
          {{ $t('Previous Page') }}
        </button>
        <span>{{ memberPage }} / {{ memberTotalPages }}</span>
        <button type="button" :disabled="memberPage >= memberTotalPages" @click="changeMemberPage(memberPage + 1)">
          {{ $t('Next Page') }}
        </button>
      </div>
    </section>

    <section v-else class="panel rebate-panel">
      <div v-if="loadingRebates" class="empty-state">{{ $t('Loading') }}...</div>
      <div v-else-if="!rebateLogs.length" class="empty-state">{{ $t('No rebate records yet') }}</div>
      <article v-for="log in rebateLogs" :key="log.id" class="rebate-log">
        <div class="log-main">
          <div class="identity compact">
            <span class="avatar">{{ userInitial(log.username) }}</span>
            <div>
              <strong>{{ log.username || (log.subordinate_id ? 'ID ' + log.subordinate_id : '-') }}</strong>
              <small>{{ log.addtime || log.bet_time || '-' }}</small>
            </div>
          </div>
          <div class="log-amount">
            <strong>+¥{{ fmt(log.money) }}</strong>
            <span :class="log.state === 1 ? 'settled' : 'waiting'">
              {{ log.state === 1 ? $t('Settled') : $t('Pending Settle') }}
            </span>
          </div>
        </div>
        <div class="log-meta">
          <span>{{ log.platform_type || '-' }}</span>
          <span>{{ $t('Valid Bet') }} ¥{{ fmt(log.valid_amount) }}</span>
          <span class="bet-id">{{ shortBetId(log.betid) }}</span>
        </div>
      </article>

      <div class="pager" v-if="rebateTotalPages > 1">
        <button type="button" :disabled="rebatePage <= 1" @click="changeRebatePage(rebatePage - 1)">
          {{ $t('Previous Page') }}
        </button>
        <span>{{ rebatePage }} / {{ rebateTotalPages }}</span>
        <button type="button" :disabled="rebatePage >= rebateTotalPages" @click="changeRebatePage(rebatePage + 1)">
          {{ $t('Next Page') }}
        </button>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'turnoverAgentCenter',
  props: {
    rebateRate: {
      type: [Number, String],
      default: 0
    },
    turnoverStats: {
      type: Object,
      default() {
        return { today: 0, total: 0, pending: 0 };
      }
    }
  },
  data() {
    return {
      activeTab: 'members',
      range: '30',
      keyword: '',
      memberPage: 1,
      memberPerPage: 10,
      memberTotal: 0,
      loadingMembers: false,
      members: [],
      summary: {
        direct_count: 0,
        today_turnover: 0,
        recharge: 0,
        turnover: 0,
        win_amount: 0,
        loss_amount: 0,
        net_win_loss: 0,
        balance: 0
      },
      rebateSummary: {
        today: this.turnoverStats.today || 0,
        total: this.turnoverStats.total || 0,
        pending: this.turnoverStats.pending || 0
      },
      rebateLogs: [],
      rebatePage: 1,
      rebatePerPage: 10,
      rebateTotal: 0,
      loadingRebates: false
    };
  },
  computed: {
    rangeOptions() {
      return [
        { value: '7', label: this.$t('Last 7 days') },
        { value: '30', label: this.$t('Last 30 days') },
        { value: 'all', label: this.$t('All time') }
      ];
    },
    rangeLabel() {
      const selected = this.rangeOptions.find(item => item.value === this.range);
      return selected ? selected.label : this.$t('Last 30 days');
    },
    memberTotalPages() {
      return Math.max(1, Math.ceil((this.memberTotal || 0) / this.memberPerPage));
    },
    rebateTotalPages() {
      return Math.max(1, Math.ceil((this.rebateTotal || 0) / this.rebatePerPage));
    }
  },
  watch: {
    turnoverStats: {
      deep: true,
      handler(value) {
        this.rebateSummary = Object.assign({}, this.rebateSummary, value || {});
      }
    }
  },
  created() {
    this.fetchMembers();
    this.fetchRebates();
  },
  methods: {
    fmt(value) {
      const number = parseFloat(value);
      return isFinite(number) ? number.toFixed(2) : '0.00';
    },
    signedNumber(value) {
      const number = parseFloat(value) || 0;
      return (number > 0 ? '+' : '') + this.fmt(number);
    },
    signedMoney(value) {
      const number = parseFloat(value) || 0;
      return (number > 0 ? '+' : (number < 0 ? '-' : '')) + '¥' + this.fmt(Math.abs(number));
    },
    netClass(value) {
      const number = parseFloat(value) || 0;
      if (number > 0) return 'win';
      if (number < 0) return 'loss';
      return '';
    },
    userInitial(username) {
      const value = String(username || 'U');
      return value.charAt(0).toUpperCase();
    },
    shortBetId(value) {
      const id = String(value || '-');
      if (id.length <= 24) return id;
      return id.slice(0, 11) + '…' + id.slice(-8);
    },
    pickRange(value) {
      if (this.range === value) return;
      this.range = value;
      this.reloadMembers();
    },
    reloadMembers() {
      this.memberPage = 1;
      this.fetchMembers();
    },
    changeMemberPage(page) {
      if (page < 1 || page > this.memberTotalPages) return;
      this.memberPage = page;
      this.fetchMembers();
    },
    changeRebatePage(page) {
      if (page < 1 || page > this.rebateTotalPages) return;
      this.rebatePage = page;
      this.fetchRebates();
    },
    fetchMembers() {
      this.loadingMembers = true;
      this.$apiFun.post('/api/invite-subordinate-stats', {
        range: this.range,
        keyword: this.keyword,
        page: this.memberPage,
        per_page: this.memberPerPage
      }).then(res => {
        this.loadingMembers = false;
        if (res.code === 200 && res.data) {
          this.members = res.data.list || [];
          this.memberTotal = res.data.total || 0;
          this.memberPage = res.data.page || this.memberPage;
          this.summary = Object.assign({}, this.summary, res.data.summary || {});
        } else {
          this.members = [];
          this.memberTotal = 0;
        }
      }).catch(() => {
        this.loadingMembers = false;
        this.members = [];
      });
    },
    fetchRebates() {
      this.loadingRebates = true;
      this.$apiFun.post('/api/invite-rebate-logs', {
        page: this.rebatePage,
        per_page: this.rebatePerPage
      }).then(res => {
        this.loadingRebates = false;
        if (res.code === 200 && res.data) {
          this.rebateLogs = res.data.list || [];
          this.rebateTotal = res.data.total || 0;
          this.rebatePage = res.data.page || this.rebatePage;
          this.rebateSummary = Object.assign({}, this.rebateSummary, res.data.turnoverStats || {});
        } else {
          this.rebateLogs = [];
          this.rebateTotal = 0;
        }
      }).catch(() => {
        this.loadingRebates = false;
        this.rebateLogs = [];
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.turnover-center {
  min-height: calc(100vh - 44px);
  padding: 14px 12px 34px;
  color: #edf1f8;
  background:
    radial-gradient(circle at 100% 0, rgba(32, 104, 230, 0.2), transparent 34%),
    #090b10;
}

.hero-card {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  padding: 18px;
  border-radius: 18px;
  background: linear-gradient(135deg, #0b55c8 0%, #173f84 56%, #151a25 100%);
  box-shadow: 0 14px 32px rgba(0, 52, 150, 0.28);

  h2 {
    margin: 9px 0 6px;
    font-size: 23px;
    line-height: 1;
  }

  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.72);
    font-size: 12px;
    line-height: 1.45;

    strong { color: #ffe487; font-size: 15px; }
  }
}

.mode-badge {
  display: inline-flex;
  padding: 4px 8px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.86);
  font-size: 10px;
}

.share-btn {
  flex-shrink: 0;
  height: 34px;
  padding: 0 13px;
  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 12px;
}

.rebate-overview {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-top: 12px;
}

.rebate-card {
  min-width: 0;
  padding: 13px 10px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 14px;
  background: #12151d;

  .card-label {
    display: block;
    margin-bottom: 8px;
    overflow: hidden;
    color: #8f98aa;
    font-size: 11px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    color: #f4f6fb;
    font-size: 17px;
    white-space: nowrap;
  }

  &.today strong { color: #ffe17c; }
  &.pending strong { color: #ffb36a; }
}

.data-overview,
.panel {
  margin-top: 12px;
  padding: 15px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 17px;
  background: rgba(18, 21, 29, 0.96);
}

.section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 13px;

  .eyebrow {
    display: block;
    margin-bottom: 3px;
    color: #677086;
    font-size: 10px;
    text-transform: uppercase;
  }

  h3 {
    margin: 0;
    color: #f3f5fa;
    font-size: 16px;
  }

  .member-count {
    padding: 5px 9px;
    border-radius: 99px;
    background: rgba(46, 119, 255, 0.14);
    color: #8ab4ff;
    font-size: 11px;
  }
}

.overview-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.overview-item {
  min-width: 0;
  padding: 10px 9px;
  border-radius: 11px;
  background: #0d1016;

  span {
    display: block;
    margin-bottom: 5px;
    overflow: hidden;
    color: #737d90;
    font-size: 10px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    display: block;
    overflow: hidden;
    color: #e8ecf4;
    font-size: 13px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &.featured strong { color: #ffe17c; }
}

.win strong, strong.win, .win > strong { color: #60d394 !important; }
.loss strong, strong.loss, .loss > strong { color: #ff7a7a !important; }

.content-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  margin-top: 14px;
  padding: 4px;
  border-radius: 13px;
  background: #12151d;

  button {
    height: 38px;
    border: none;
    border-radius: 10px;
    background: transparent;
    color: #7e8798;
    font-size: 13px;
    font-weight: 600;

    &.active {
      background: linear-gradient(135deg, #215fcb, #17438d);
      color: #fff;
      box-shadow: 0 5px 15px rgba(25, 87, 192, 0.3);
    }
  }
}

.filters {
  padding-bottom: 13px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.range-tabs {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  margin-bottom: 9px;

  button {
    height: 31px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 9px;
    background: #0d1016;
    color: #7f899c;
    font-size: 11px;

    &.active {
      border-color: rgba(65, 126, 234, 0.65);
      background: rgba(35, 91, 185, 0.2);
      color: #9fc0ff;
    }
  }
}

.search-row {
  display: flex;
  gap: 7px;

  input {
    flex: 1;
    min-width: 0;
    height: 35px;
    padding: 0 11px;
    border: 1px solid rgba(255, 255, 255, 0.09);
    border-radius: 9px;
    outline: none;
    background: #0d1016;
    color: #e8ecf4;
    font-size: 12px;
  }

  button {
    flex-shrink: 0;
    width: 58px;
    border: none;
    border-radius: 9px;
    background: #225dc0;
    color: #fff;
    font-size: 12px;
  }
}

.member-card,
.rebate-log {
  margin-top: 10px;
  padding: 13px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  background: #0d1016;
}

.member-head,
.log-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.identity {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 9px;

  .avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 34px;
    width: 34px;
    height: 34px;
    border-radius: 11px;
    background: linear-gradient(135deg, #2a6bd5, #183c79);
    color: #fff;
    font-size: 14px;
    font-weight: 700;
  }

  div { min-width: 0; }

  strong {
    display: block;
    overflow: hidden;
    color: #f2f5fa;
    font-size: 14px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small {
    display: block;
    margin-top: 3px;
    color: #687287;
    font-size: 10px;
  }

  &.compact .avatar {
    flex-basis: 31px;
    width: 31px;
    height: 31px;
    border-radius: 50%;
  }
}

.member-head time {
  flex-shrink: 0;
  color: #667084;
  font-size: 9px;
  line-height: 1.45;
  text-align: right;
}

.member-metrics {
  display: grid;
  gap: 6px;
  margin-top: 10px;

  div {
    min-width: 0;
    padding: 8px 7px;
    border-radius: 9px;
    background: #121620;
  }

  span {
    display: block;
    margin-bottom: 4px;
    overflow: hidden;
    color: #6f798c;
    font-size: 9px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    display: block;
    overflow: hidden;
    color: #dfe4ed;
    font-size: 11px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.primary-metrics { grid-template-columns: repeat(3, 1fr); }
.result-metrics { grid-template-columns: repeat(4, 1fr); }

.log-amount {
  flex-shrink: 0;
  text-align: right;

  strong {
    display: block;
    color: #ffe17c;
    font-size: 15px;
  }

  span {
    display: block;
    margin-top: 3px;
    font-size: 10px;
  }

  .settled { color: #60d394; }
  .waiting { color: #ffb36a; }
}

.log-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;

  span {
    max-width: 100%;
    padding: 4px 7px;
    border-radius: 6px;
    background: #151a24;
    color: #7f899b;
    font-size: 9px;
  }

  .bet-id {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.empty-state {
  padding: 42px 10px;
  color: #626c7e;
  font-size: 12px;
  text-align: center;
}

.pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 14px;

  button {
    height: 31px;
    padding: 0 12px;
    border: 1px solid rgba(255, 255, 255, 0.09);
    border-radius: 8px;
    background: #111722;
    color: #a9b3c5;
    font-size: 11px;

    &:disabled { opacity: 0.35; }
  }

  span {
    color: #687286;
    font-size: 11px;
  }
}

@media (max-width: 360px) {
  .turnover-center { padding-left: 9px; padding-right: 9px; }
  .hero-card { padding: 15px; }
  .share-btn { padding: 0 9px; }
  .rebate-card strong { font-size: 14px; }
  .overview-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .result-metrics { grid-template-columns: repeat(2, 1fr); }
}
</style>



// WEBPACK FOOTER //
// src/components/agent/turnoverAgentCenter.vue